import { useState, useEffect, useCallback } from "react";
import { Input } from "./input";
import { cn } from "./utils";
import { Search } from "lucide-react";

interface ExpandableSearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export function ExpandableSearchInput({
  value,
  onChange,
  placeholder = "Search by purpose, reference, notes...",
  className,
  disabled = false,
}: ExpandableSearchInputProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [inputValue, setInputValue] = useState(value);

  // Sync external value changes
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Handle focus - expand input
  const handleFocus = useCallback(() => {
    setIsExpanded(true);
    setIsFocused(true);
  }, []);

  // Handle blur - collapse input with delay
  const handleBlur = useCallback(() => {
    setIsFocused(false);
    // Small delay to allow click events to complete
    setTimeout(() => {
      setIsExpanded(false);
    }, 200);
  }, []);

  // Handle click on container - expand and focus input
  const handleContainerClick = useCallback(() => {
    setIsExpanded(true);
    // Focus the input directly
    const input = document.querySelector(`[data-search-input="true"]`) as HTMLInputElement;
    if (input) {
      input.focus();
    }
  }, []);

  // Handle input change
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange(newValue);

    // Auto-expand when user starts typing if not already expanded
    if (!isExpanded && newValue.length > 0) {
      setIsExpanded(true);
    }
  }, [isExpanded, onChange]);

  // Handle keydown
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsExpanded(false);
      e.currentTarget.blur();
    }
  }, []);

  return (
    <div
      className={cn(
        "relative transition-all duration-300 ease-in-out z-10",
        "min-w-[192px]",
        isExpanded
          ? "!w-[300px] max-w-[300px]" // 300px - half of previous size
          : "!w-48 max-w-48", // 192px - fits ~3-4 words
        className
      )}
      onClick={handleContainerClick}
      style={{
        width: isExpanded ? '300px' : '192px',
        maxWidth: isExpanded ? '300px' : '192px',
        minWidth: isExpanded ? '300px' : '192px'
      }}
    >
      {/* Search icon */}
      <Search
        className={cn(
          "absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground transition-colors duration-200 z-20",
          isFocused ? "text-blue-500" : ""
        )}
      />

      {/* Input field */}
      <Input
        data-search-input="true"
        type="text"
        placeholder={placeholder}
        value={inputValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className={cn(
          // Base styling
          "pl-9 pr-3", // Standard right padding, no character counter
          "py-2 w-full h-10",
          // Border and focus states
          "border border-gray-300 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent",
          // Background
          "bg-white dark:bg-gray-800",
          // Text styling
          "text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400",
          // Transitions
          "transition-all duration-300 ease-in-out",
          // Focus effects
          isFocused && "shadow-sm ring-1 ring-blue-500/10",
          // Disabled state
          disabled && "opacity-50 cursor-not-allowed",
          // Ensure cursor pointer on container click
          "cursor-text",
          className
        )}
      />
    </div>
  );
}